import React, { useState, useEffect } from 'react';
import { Play, Pause, Clock, Square } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

// =============================================
// CHRONO LAUNCHER - Bouton compact pour démarrer/arrêter un chrono
// Utilisable dans les tâches et interventions
// =============================================

const ChronoLauncher = ({ 
  taskId, 
  projectId, 
  contractId,
  taskTitle,
  size = 'default', // 'sm' | 'default'
  variant = 'outline', // 'outline' | 'ghost'
  showLabel = true,
  onTimerUpdate
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentTimer, setCurrentTimer] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(false);

  // Check if there's an active timer for this task
  useEffect(() => {
    checkActiveTimer();
    const interval = setInterval(checkActiveTimer, 30000);
    return () => clearInterval(interval);
  }, [taskId]);

  // Update elapsed time
  useEffect(() => {
    let interval;
    if (currentTimer && isRunning) {
      interval = setInterval(() => {
        const startTime = new Date(currentTimer.start_time);
        const now = new Date();
        const diffMs = now - startTime;
        setElapsed(Math.floor(diffMs / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentTimer, isRunning]);

  const checkActiveTimer = async () => {
    try {
      const response = await api.get('/time/current');
      if (response.data && response.data.task_id === taskId) {
        setCurrentTimer(response.data);
        setIsRunning(true);
      } else {
        setCurrentTimer(null);
        setIsRunning(false);
        setElapsed(0);
      }
    } catch (error) {
      // No active timer
      setIsRunning(false);
    }
  };

  const startTimer = async () => {
    setLoading(true);
    try {
      const response = await api.post('/time/start', {
        task_id: taskId,
        project_id: projectId,
        contract_id: contractId,
        description: taskTitle || 'Travail sur tâche'
      });
      
      setCurrentTimer(response.data);
      setIsRunning(true);
      toast.success('Chrono démarré');
      onTimerUpdate?.();
    } catch (error) {
      console.error('Start timer error:', error);
      if (error.response?.data?.detail?.includes('already running')) {
        toast.error('Un chrono est déjà en cours');
      } else {
        toast.error('Erreur lors du démarrage');
      }
    } finally {
      setLoading(false);
    }
  };

  const stopTimer = async () => {
    if (!currentTimer) return;
    
    setLoading(true);
    try {
      await api.post('/time/stop', {
        notes: `Arrêt chrono - ${taskTitle || 'Tâche'}`
      });
      
      setCurrentTimer(null);
      setIsRunning(false);
      setElapsed(0);
      toast.success('Chrono arrêté et enregistré');
      onTimerUpdate?.();
    } catch (error) {
      console.error('Stop timer error:', error);
      toast.error('Erreur lors de l\'arrêt');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const buttonSize = size === 'sm' ? 'h-7 px-2 text-xs' : 'h-9 px-3 text-sm';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';

  if (isRunning) {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="destructive" className="animate-pulse gap-1 font-mono">
          <Clock className={iconSize} />
          {formatTime(elapsed)}
        </Badge>
        <Button
          variant="destructive"
          size="sm"
          onClick={stopTimer}
          disabled={loading}
          className={cn(buttonSize)}
        >
          <Square className={cn(iconSize, "mr-1")} />
          {showLabel && 'Stop'}
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant={variant}
      size="sm"
      onClick={startTimer}
      disabled={loading}
      className={cn(buttonSize, "gap-1")}
    >
      <Play className={iconSize} />
      {showLabel && 'Chrono'}
    </Button>
  );
};

export default ChronoLauncher;
